import { readdir, readFile, writeFile, mkdir } from 'node:fs/promises';
import { join, relative } from 'node:path';
import { existsSync } from 'node:fs';

const IGNORED_DIRS = new Set([
  'node_modules', '.git', 'dist', '.turbo', 'coverage', '.next', 'out', 'build',
  'AppData', 'Application Data', 'Local Settings', 'Temp', '$Recycle.Bin', 'System Volume Information',
  '.npm', '.pnpm', '.cache', '.vscode', '.idea'
]);
const IGNORED_FILES = new Set(['pnpm-lock.yaml', 'yarn.lock', 'package-lock.json', '.env', '.env.example', '.env.local', 'pnpm-workspace.yaml']);
const ALLOWED_EXTENSIONS = new Set(['.ts', '.tsx', '.js', '.jsx', '.json', '.md', '.css']);

export interface RepoMapSymbol {
  name: string;
  type: 'class' | 'interface' | 'type' | 'function';
  line: number;
}

export interface RepoMapFile {
  path: string;
  symbols: RepoMapSymbol[];
}

async function scanDirectory(root: string, current: string, files: string[]): Promise<void> {
  let entries;
  try {
    entries = await readdir(current, { withFileTypes: true });
  } catch {
    // Skip directories we do not have permission to read (EPERM, EACCES, ENOENT)
    return;
  }
  for (const entry of entries) {
    const name = entry.name;
    if (entry.isDirectory()) {
      if (IGNORED_DIRS.has(name)) continue;
      await scanDirectory(root, join(current, name), files);
    } else {
      if (IGNORED_FILES.has(name)) continue;
      const ext = name.substring(name.lastIndexOf('.')).toLowerCase();
      if (ALLOWED_EXTENSIONS.has(ext)) {
        files.push(join(current, name));
      }
    }
  }
}

export function parseSymbolsFromCode(content: string): RepoMapSymbol[] {
  const lines = content.split('\n');
  const symbols: RepoMapSymbol[] = [];

  for (let i = 0; i < lines.length; i++) {
    const line = lines[i];
    if (!line) continue;
    const lineNum = i + 1;

    // Class check
    const classMatch = line.match(/^\s*(export\s+)?(default\s+)?class\s+(\w+)/);
    if (classMatch && classMatch[3]) {
      symbols.push({ name: classMatch[3], type: 'class', line: lineNum });
      continue;
    }

    // Interface check
    const interfaceMatch = line.match(/^\s*(export\s+)?interface\s+(\w+)/);
    if (interfaceMatch && interfaceMatch[2]) {
      symbols.push({ name: interfaceMatch[2], type: 'interface', line: lineNum });
      continue;
    }

    // Type check
    const typeMatch = line.match(/^\s*(export\s+)?type\s+(\w+)/);
    if (typeMatch && typeMatch[2]) {
      symbols.push({ name: typeMatch[2], type: 'type', line: lineNum });
      continue;
    }

    // Function check
    const funcMatch = line.match(/^\s*(export\s+)?(default\s+)?(async\s+)?function\s+(\w+)/);
    if (funcMatch && funcMatch[4]) {
      symbols.push({ name: funcMatch[4], type: 'function', line: lineNum });
      continue;
    }

    // Arrow function / constant function check
    const arrowFuncMatch = line.match(/^\s*(export\s+)?(const|let)\s+(\w+)\s*=\s*(async\s*)?\(.*?\)\s*=>/);
    if (arrowFuncMatch && arrowFuncMatch[3]) {
      symbols.push({ name: arrowFuncMatch[3], type: 'function', line: lineNum });
      continue;
    }
  }

  return symbols;
}

export async function generateRepositoryMap(workspaceRoot: string): Promise<RepoMapFile[]> {
  const files: string[] = [];
  await scanDirectory(workspaceRoot, workspaceRoot, files);

  const mapFiles: RepoMapFile[] = [];

  for (const filePath of files) {
    const relPath = relative(workspaceRoot, filePath).replace(/\\/g, '/');
    const ext = filePath.substring(filePath.lastIndexOf('.')).toLowerCase();
    
    let symbols: RepoMapSymbol[] = [];
    if (['.ts', '.tsx', '.js', '.jsx'].includes(ext)) {
      try {
        const content = await readFile(filePath, 'utf8');
        symbols = parseSymbolsFromCode(content);
      } catch {
        // Skip unreadable files
      }
    }
    mapFiles.push({ path: relPath, symbols });
  }

  // Save the map to .turbo/vexalyn-map.json
  const outDir = join(workspaceRoot, '.turbo');
  if (!existsSync(outDir)) {
    await mkdir(outDir, { recursive: true });
  }
  await writeFile(join(outDir, 'vexalyn-map.json'), JSON.stringify(mapFiles, null, 2), 'utf8');

  return mapFiles;
}

export function formatCompactRepositoryMap(mapFiles: RepoMapFile[]): string {
  let summary = 'REPOSITORY MAP (CODEBASE ATLAS):\n';
  const codeFiles = mapFiles.filter(f => f.symbols.length > 0);
  
  for (const file of codeFiles) {
    summary += `- ${file.path}:\n`;
    for (const sym of file.symbols) {
      summary += `    ${sym.type} ${sym.name}\n`;
    }
  }
  return summary;
}
